import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, FileText, CheckCircle, Loader } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Confirmation Screen ───────────────────────────────────────────────────────
function ConfirmationScreen({ name, packageName, version, signedAt, onContinue, continuing }) {
  return (
    <div className="bg-ivory min-h-screen flex flex-col">
      {/* Header */}
      <div className="bg-navy py-12 px-6 text-center">
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="flex items-center justify-center mb-5"
        >
          <div className="w-14 h-14 rounded-full border border-gold/40 flex items-center justify-center">
            <CheckCircle size={26} className="text-gold" />
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <p className="text-gold text-xs tracking-[0.2em] uppercase mb-3">Agreement Confirmed</p>
          <h1 className="text-ivory font-lora text-2xl sm:text-3xl font-normal">
            Your agreement has been<br className="hidden sm:block" /> securely recorded.
          </h1>
          <p className="text-ivory/50 text-sm mt-3 font-serif">
            You may now proceed to booking and payment.
          </p>
        </motion.div>
      </div>

      {/* Summary card */}
      <div className="flex-1 flex items-start justify-center px-6 py-14">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="w-full max-w-md"
        >
          <div className="bg-white border border-soft-gray">
            {/* Card header */}
            <div className="px-8 py-5 border-b border-soft-gray">
              <p className="text-navy/40 text-xs tracking-[0.18em] uppercase font-sans">Agreement Summary</p>
            </div>

            {/* Fields */}
            <div className="divide-y divide-soft-gray/60">
              {[
                { label: "Name", value: name },
                { label: "Package", value: packageName },
                { label: "Agreement Version", value: version },
                { label: "Date Signed", value: signedAt },
              ].map(({ label, value }) => (
                <div key={label} className="px-8 py-4 flex items-baseline justify-between gap-4">
                  <span className="text-navy/40 text-xs tracking-widest uppercase font-sans flex-shrink-0">
                    {label}
                  </span>
                  <span className="text-navy text-sm font-serif text-right">{value}</span>
                </div>
              ))}
            </div>

            {/* Footer note */}
            <div className="px-8 py-5 bg-[#F8F7F3] border-t border-soft-gray">
              <p className="text-navy/40 text-xs font-serif text-center">
                A copy has been sent to your email address.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-8">
            <button
              onClick={onContinue}
              disabled={continuing}
              className="w-full py-4 bg-navy text-ivory text-sm tracking-[0.15em] uppercase font-sans hover:bg-navy/90 active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="agreement-continue-btn"
            >
              {continuing ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader size={14} className="animate-spin" /> Preparing your booking...
                </span>
              ) : (
                "Continue to Booking"
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function AgreementPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state;

  useEffect(() => {
    if (!state?.service || !state?.date || !state?.timeSlot) {
      navigate("/booking", { replace: true });
    }
  }, [state, navigate]);

  const [agreementText, setAgreementText] = useState("");
  const [agreementVersion, setAgreementVersion] = useState("");
  const [loading, setLoading] = useState(true);

  const [fullName, setFullName] = useState(state?.clientName || "");
  const [email, setEmail] = useState(state?.clientEmail || "");
  const [signature, setSignature] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);

  // Confirmation state
  const [confirmed, setConfirmed] = useState(false);
  const [acceptanceId, setAcceptanceId] = useState(null);
  const [signedAt, setSignedAt] = useState("");
  const [continuing, setContinuing] = useState(false);

  // Intentional: empty dep array fetches the agreement text once on mount; re-running would overwrite the user's in-progress form state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetch(`${API}/api/agreements/text`)
      .then(r => r.json())
      .then(d => {
        setAgreementText(d.text || "");
        setAgreementVersion(d.version || "");
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 30) {
      setScrolledToBottom(true);
    }
  };

  const canSubmit =
    fullName.trim().length > 1 &&
    email.trim().length > 4 &&
    signature.trim().length > 1 &&
    agreed;

  // Step 1: Sign the agreement → show confirmation screen
  const handleSign = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/agreements/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          full_name: fullName.trim(),
          email: email.trim(),
          typed_signature: signature.trim(),
          package_id: state.service.id,
          agreed: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Agreement signing failed");

      setAcceptanceId(data.acceptance_id);
      setSignedAt(data.signed_at);
      setConfirmed(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Step 2: Continue to booking → create booking → redirect
  const handleContinue = async () => {
    if (!acceptanceId) return;
    setContinuing(true);
    try {
      const res = await fetch(`${API}/api/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          service_id: state.service.id,
          date: state.date,
          time_slot: state.timeSlot,
          client_name: fullName.trim(),
          client_email: email.trim(),
          intake_situation: state.intake || null,
          origin_url: window.location.origin,
          acceptance_id: acceptanceId,
          source: state.source || "direct",
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Booking failed");

      if (data.free) {
        navigate(
          `/booking/success?free=true&service_name=${encodeURIComponent(state.service.name)}&date=${encodeURIComponent(state.formattedDate)}&time=${encodeURIComponent(state.timeSlot)}&name=${encodeURIComponent(fullName)}`,
          { replace: true }
        );
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      setContinuing(false);
      setError(e.message || "Something went wrong. Please try again.");
      setConfirmed(false); // Return to form so user can retry
    }
  };

  if (!state?.service) return null;

  // ── Show confirmation screen after signing ──
  if (confirmed) {
    return (
      <ConfirmationScreen
        name={fullName}
        packageName={state.service.name}
        version={agreementVersion || "v1.1 - May 2026"}
        signedAt={signedAt}
        onContinue={handleContinue}
        continuing={continuing}
      />
    );
  }

  const checkboxLabel = `I have read, understood, and agree to be bound by the De Nicola Consulting Co. Client Services Agreement and Liability Waiver, ${agreementVersion || "v1.1 - May 2026"}.`;

  return (
    <div className="bg-ivory min-h-screen">
      {/* Header */}
      <div className="bg-navy py-10 px-6 text-center relative">
        <button
          onClick={() => navigate(-1)}
          className="absolute left-6 top-10 flex items-center gap-1 text-ivory/50 hover:text-ivory text-xs tracking-widest uppercase transition-colors"
          data-testid="agreement-back-btn"
        >
          <ChevronLeft size={14} /> Back
        </button>
        <div className="flex items-center justify-center gap-2 mb-3">
          <FileText size={16} className="text-gold" />
          <p className="text-gold text-xs tracking-[0.2em] uppercase">Client Services Agreement</p>
        </div>
        <h1 className="text-ivory font-lora text-2xl font-normal">Review &amp; Sign Agreement</h1>
        <p className="text-ivory/50 text-sm mt-2 font-serif">Required before proceeding to payment</p>
      </div>

      {/* Booking summary strip */}
      <div className="bg-[#F4F3EF] border-b border-gold/20 px-6 py-4 text-center">
        <p className="text-navy/60 text-xs tracking-widest uppercase font-serif">
          {state.service.name}
          {state.service.price > 0 ? ` — $${state.service.price} CAD` : " — Complimentary"}
          {" · "}{state.formattedDate}{" · "}{state.timeSlot}
        </p>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Agreement text container */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-navy font-lora text-base font-normal tracking-wide">Client Services Agreement</h2>
            {agreementVersion && (
              <span className="text-navy/40 text-xs tracking-widest uppercase">{agreementVersion}</span>
            )}
          </div>

          {loading ? (
            <div className="h-96 bg-white border border-soft-gray flex items-center justify-center">
              <Loader size={20} className="text-gold animate-spin" />
            </div>
          ) : (
            <>
              <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="h-96 overflow-y-auto border border-soft-gray bg-white p-6 font-serif text-sm text-navy/70 leading-relaxed whitespace-pre-wrap"
                data-testid="agreement-text-container"
              >
                {agreementText}
              </div>
              {!scrolledToBottom && (
                <p className="text-navy/40 text-xs text-center mt-2 font-serif italic">
                  Scroll to read the full agreement
                </p>
              )}
            </>
          )}
        </div>

        {/* Signing form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white border border-soft-gray p-8"
        >
          <h3 className="text-navy font-lora text-base font-normal mb-6 tracking-wide">Electronic Signature</h3>

          <div className="space-y-5">
            <div>
              <label className="block text-navy/60 text-xs tracking-widest uppercase mb-1.5">
                Full Legal Name <span className="text-gold">*</span>
              </label>
              <input
                type="text"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                placeholder="Enter your full legal name"
                className="w-full border border-soft-gray bg-ivory px-4 py-2.5 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors"
                data-testid="agreement-full-name"
              />
            </div>

            <div>
              <label className="block text-navy/60 text-xs tracking-widest uppercase mb-1.5">
                Email Address <span className="text-gold">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full border border-soft-gray bg-ivory px-4 py-2.5 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors"
                data-testid="agreement-email"
              />
            </div>

            <div>
              <label className="block text-navy/60 text-xs tracking-widest uppercase mb-1.5">
                Electronic Signature — Type your full name <span className="text-gold">*</span>
              </label>
              <input
                type="text"
                value={signature}
                onChange={e => setSignature(e.target.value)}
                placeholder="Type your full name as your electronic signature"
                className="w-full border border-soft-gray bg-ivory px-4 py-2.5 text-navy font-serif text-sm italic focus:outline-none focus:border-navy transition-colors"
                data-testid="agreement-signature"
              />
              <p className="text-navy/40 text-xs mt-1 font-serif">
                By typing your name above, you are signing this agreement electronically.
              </p>
            </div>

            <div className="border-t border-soft-gray pt-5">
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative mt-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={e => setAgreed(e.target.checked)}
                    className="sr-only"
                    data-testid="agreement-checkbox"
                  />
                  <div
                    onClick={() => setAgreed(!agreed)}
                    className={`w-5 h-5 border-2 flex items-center justify-center transition-all cursor-pointer ${
                      agreed ? "bg-navy border-navy" : "bg-white border-navy/30 group-hover:border-navy/60"
                    }`}
                  >
                    {agreed && <CheckCircle size={12} className="text-ivory" />}
                  </div>
                </div>
                <span className="text-navy/70 text-sm font-serif leading-relaxed" data-testid="agreement-checkbox-label">
                  {checkboxLabel}
                </span>
              </label>
            </div>
          </div>

          {error && (
            <p className="mt-4 text-red-600 text-sm font-serif" data-testid="agreement-error">
              {error}
            </p>
          )}

          <div className="mt-8">
            <button
              onClick={handleSign}
              disabled={!canSubmit || submitting}
              className={`w-full py-4 text-sm tracking-[0.15em] uppercase font-sans transition-all ${
                canSubmit && !submitting
                  ? "bg-navy text-ivory hover:bg-navy/90 cursor-pointer"
                  : "bg-navy/30 text-ivory/50 cursor-not-allowed"
              }`}
              data-testid="agreement-submit-btn"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader size={14} className="animate-spin" /> Saving Agreement...
                </span>
              ) : (
                "Agree and Continue"
              )}
            </button>
            <p className="text-navy/40 text-xs text-center mt-3 font-serif">
              Your signed agreement will be emailed to you immediately.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
